/**
 * useLoanChat — state + transport for a single loan's live chat.
 *
 * One hook drives the whole chat panel:
 *   • Loads durable history (GET /chat/{dealId}/messages) on mount / dealId change.
 *   • LIVE mode (Web PubSub connected): joins the `chat:{dealId}` room and appends
 *     `chat_message` events fanned out by RealtimeProvider; leaves on unmount.
 *   • FALLBACK mode (Web PubSub unprovisioned/disconnected — the current state per
 *     realtime-web-pubsub.md): polls the history endpoint and merges new messages.
 *   • send() persists + broadcasts; the POST response is merged optimistically so
 *     the sender sees their message immediately even when live push is dark.
 *
 * Messages are de-duped by id and kept oldest-first. Returns enough state for the
 * panel to render a status pill, a scroll-back list, and a composer.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { loanChatApi } from "../api/loanChatApi";
import { useRealtime } from "./RealtimeProvider";

// How often to re-pull history when there's no live socket.
const POLL_MS = 15000;

// Mongo ids may arrive as a bare string or an extended-JSON `{ $oid }`.
const oid = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object") return v.$oid || v.oid || v._id || "";
  return String(v);
};

const asIso = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object") return v.$date || v.date || "";
  return String(v);
};

export const normalizeChatMessage = (m) => {
  if (!m || typeof m !== "object") return null;
  const id = String(oid(m._id) || m.id || "");
  return {
    id,
    senderEmail: (m.senderEmail || "").toLowerCase(),
    senderName: m.senderName || m.senderEmail || "Unknown",
    htmlString: m.htmlString || m.text || "",
    sentAt: asIso(m.sentAt),
    mentions: m.mentions || [],
  };
};

// Plain text → minimal safe HTML for the htmlString payload (escape, keep breaks).
const escapeHtml = (s) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

export const textToHtmlString = (text) =>
  `<p>${escapeHtml(text).replace(/\r?\n/g, "<br/>")}</p>`;

export function useLoanChat(dealId, { channel = "general", enabled = true } = {}) {
  const { connectionId, status, subscribeChat } = useRealtime();
  const live = status === "connected" && !!connectionId;

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Ids we've already shown, so realtime + polling + optimistic sends don't double.
  const idsRef = useRef(new Set());

  const mergeIn = useCallback((incoming) => {
    if (!Array.isArray(incoming) || !incoming.length) return;
    setMessages((prev) => {
      let added = false;
      const next = prev.slice();
      for (const raw of incoming) {
        const m = normalizeChatMessage(raw);
        if (!m || !m.id || idsRef.current.has(m.id)) continue;
        idsRef.current.add(m.id);
        next.push(m);
        added = true;
      }
      if (!added) return prev;
      next.sort((a, b) => (a.sentAt || "").localeCompare(b.sentAt || ""));
      return next;
    });
  }, []);

  // ── History load (re-runs on dealId / channel change) ──────────────────────
  useEffect(() => {
    if (!enabled || !dealId) return undefined;
    let cancelled = false;
    idsRef.current = new Set();
    setMessages([]);
    setError(null);
    setLoading(true);
    loanChatApi
      .getMessages(dealId, { channel, limit: 100 })
      .then((d) => {
        if (!cancelled) mergeIn(d?.messages || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dealId, channel, enabled, mergeIn]);

  // ── LIVE: subscribe to fanned-out chat events + join/leave the room ─────────
  useEffect(() => {
    if (!enabled || !dealId || !live) return undefined;
    const unsubscribe = subscribeChat(dealId, (ev) => {
      if (ev?.channel && ev.channel !== channel) return;
      if (ev?.message) mergeIn([ev.message]);
    });
    let joined = false;
    loanChatApi
      .join(dealId, connectionId, channel)
      .then(() => {
        joined = true;
      })
      .catch(() => {
        // 503 (unprovisioned) or transient — history + send still work.
      });
    return () => {
      unsubscribe();
      if (joined) loanChatApi.leave(dealId, connectionId, channel).catch(() => {});
    };
  }, [enabled, dealId, channel, live, connectionId, subscribeChat, mergeIn]);

  // ── FALLBACK: poll history while there's no live socket ─────────────────────
  // Only runs while the hook is mounted (i.e. the chat panel is open) AND the
  // browser tab is visible — a hidden tab / closed panel polls nothing.
  useEffect(() => {
    if (!enabled || !dealId || live) return undefined;
    const isHidden = () =>
      typeof document !== "undefined" && document.hidden;
    const poll = () => {
      if (isHidden()) return;
      loanChatApi
        .getMessages(dealId, { channel, limit: 50 })
        .then((d) => mergeIn(d?.messages || []))
        .catch(() => {});
    };
    const t = setInterval(poll, POLL_MS);
    // Catch up once when the tab regains focus (instead of waiting a full cycle).
    const onVisible = () => {
      if (!isHidden()) poll();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, dealId, channel, live, mergeIn]);

  const send = useCallback(
    async (text) => {
      const trimmed = (text || "").trim();
      if (!trimmed || !dealId) return;
      setSending(true);
      try {
        const res = await loanChatApi.sendMessage(dealId, {
          htmlString: textToHtmlString(trimmed),
          channel,
        });
        // Echo the persisted message immediately (covers the live-push-dark case).
        if (res?.message) mergeIn([res.message]);
      } finally {
        setSending(false);
      }
    },
    [dealId, channel, mergeIn],
  );

  const transport = useMemo(
    () => (live ? "live" : status === "connecting" ? "connecting" : "polling"),
    [live, status],
  );

  return { messages, loading, sending, error, send, live, transport, status };
}

export default useLoanChat;
